/**
 * useStoreActions Hook
 * 
 * Encapsulates all store-level mutations for Proscriptive Workflow v1.0
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useTransitionQueue } from '@/hooks/useTransitionQueue';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

interface StartVisitParams {
  storeId: number;
  operatorId: number;
  cycleId: number;
}

interface CompleteStoreParams {
  storeId: number;
  operatorId: number;
  cycleId: number;
}

export function useStoreActions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { enqueue } = useTransitionQueue();
  const isOnline = useNetworkStatus();

  const startVisit = useMutation({
    mutationFn: async ({ storeId, operatorId, cycleId }: StartVisitParams) => {
      // If offline, queue the transition
      if (!isOnline) {
        await enqueue('STORE_START', storeId, operatorId, cycleId);
        toast({
          title: 'Queued for offline',
          description: 'Store start will be processed when online',
        });
        // Return a mock success response
        return { storeVisitId: 0, storeId, status: 'queued' };
      }

      const response = await apiFetch(`/api/mobile/store-start/${storeId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ operatorId, cycleId }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to start visit' }));
        throw new Error(error.message || error.error || 'Failed to start visit');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storeVisits'] });
      queryClient.invalidateQueries({ queryKey: ['storeVisit'] });
      toast({
        title: 'Store visit started',
        description: 'You can now work on this store',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Cannot start visit',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const completeStore = useMutation({
    mutationFn: async ({ storeId, operatorId, cycleId }: CompleteStoreParams) => {
      // If offline, queue the transition
      if (!isOnline) {
        await enqueue('STORE_COMPLETE', storeId, operatorId, cycleId);
        toast({
          title: 'Queued for offline',
          description: 'Store completion will be processed when online',
        });
        // Return a mock success response
        return { storeVisitId: 0, storeId, status: 'queued' };
      }

      const response = await apiFetch(`/api/mobile/store-complete/${storeId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ operatorId, cycleId }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to complete store' }));
        throw new Error(error.message || error.error || 'Failed to complete store');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storeVisits'] });
      queryClient.invalidateQueries({ queryKey: ['storeVisit'] });
      queryClient.invalidateQueries({ queryKey: ['completion'] });
      toast({
        title: 'Store marked complete',
        description: 'Ready for manager signature',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Cannot complete store',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    startVisit,
    completeStore,
  };
}

